import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  LOCALE_ID,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepicker } from '@angular/material/datepicker';
import moment from 'moment';

import Moment = moment.Moment;

@Component({
  selector: 'app-month-datepicker',
  templateUrl: 'month-datepicker.component.html',
  styleUrls: ['month-datepicker.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [LOCALE_ID, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'MM/YYYY',
        },
        display: {
          dateInput: 'MM/YYYY',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonthDatepickerComponent implements OnInit {
  private _maxDate: string = moment().toISOString();
  get maxDate(): string {
    return this._maxDate;
  }
  @Input() set maxDate(value: string) {
    if (value) {
      this._maxDate = value;
    } else {
      this._maxDate = undefined;
    }
  }

  private _minDate: string;
  get minDate(): string {
    return this._minDate;
  }
  @Input() set minDate(value: string) {
    if (value) {
      this._minDate = value;
    }
  }

  @Input() navigationArrows = true;
  @Input() gtmCategory: string;
  datepickerDate = new FormControl<moment.Moment>(moment());

  @Output() dateChanged = new EventEmitter<Moment>();

  ngOnInit(): void {
    this.resetDate();
  }

  resetDate(): void {
    this.datepickerDate = new FormControl<moment.Moment>(moment());
  }

  onSelectMonth(normalizedMonth: Moment, datepicker: MatDatepicker<any>): void {
    if (this.minDate && normalizedMonth.isBefore(moment(this.minDate))) {
      this.datepickerDate.setValue(moment(this.minDate));
    } else {
      this.datepickerDate.setValue(normalizedMonth);
    }
    datepicker.close();

    this.dateChanged.emit(this.datepickerDate.value);
  }

  canSelectNextMonth(): boolean {
    if (this.maxDate) {
      const nextMonthDate = moment(this.datepickerDate.value).startOf('month').add(1, 'M');
      const isAfterMaxDate = moment(nextMonthDate).isAfter(moment(this.maxDate).startOf('month'));

      return !isAfterMaxDate;
    } else {
      return true;
    }
  }

  canSelectPreviousMonth(): boolean {
    if (this.minDate) {
      const previousMonthDate = moment(this.datepickerDate.value).startOf('month').subtract(1, 'M');
      const isBeforeMinDate = moment(previousMonthDate).isBefore(
        moment(this.minDate).startOf('month')
      );

      return !isBeforeMinDate;
    } else {
      return true;
    }
  }

  onNextMonth(): void {
    if (this.canSelectNextMonth()) {
      const nextMonthDate = moment(this.datepickerDate.value).add(1, 'M').startOf('month');
      this.datepickerDate.setValue(nextMonthDate);
      this.dateChanged.emit(this.datepickerDate.value);
    }
  }

  onPreviousMonth(): void {
    if (this.canSelectPreviousMonth()) {
      const previousMonthDate = moment(this.datepickerDate.value).subtract(1, 'M').startOf('month');
      if (this.minDate && previousMonthDate.isBefore(moment(this.minDate))) {
        this.datepickerDate.setValue(moment(this.minDate));
      } else {
        this.datepickerDate.setValue(previousMonthDate);
      }
      this.dateChanged.emit(this.datepickerDate.value);
    }
  }
}
